import { showAlert } from './toast';
/**
 * mapFieldErrors — extract KRP-standard 422 field_errors from axios error
 * and convert to a Record keyed by camelCase field name suitable for setFormErrors().
 *
 * Backend response shape (BUG-S1162-001 fix):
 *   {
 *     success: false,
 *     error: {
 *       code: "VALIDATION_ERROR",
 *       details: {
 *         field_errors: [
 *           { field: "body -> tax_id", error_type: "value_error", message: "..." }
 *         ]
 *       }
 *     }
 *   }
 *
 * Returns null if no parseable field_errors found, so callers can fall back
 * to generic toast / legacy detail handling.
 */
export interface FieldErrorMap {
  [fieldName: string]: string;
}

const snakeToCamel = (s: string): string =>
  s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

/**
 * BUG-S132-PYDANTIC-EN: ข้อความ validation ที่ Pydantic สร้างเองเป็นภาษาอังกฤษดิบ และถูก
 * render ลงใต้ฟิลด์ตรงๆ ทั้งที่ UI เป็นภาษาไทยล้วน — ผู้ใช้ไทยเห็น
 * "String should have at most 200 characters" / "value is not a valid email address: ..."
 * (พิสูจน์สดบนฟอร์มเพิ่มบริษัท S132) ต่างจากข้อความที่ backend เขียนเอง ซึ่งเป็นไทยครบ
 * เช่น "เลขประจำตัวผู้เสียภาษีต้องมี 13 หลัก (ปัจจุบัน 5 หลัก)".
 * แปลเฉพาะ pattern มาตรฐานของ Pydantic v2 — ข้อความไทยจาก backend ผ่านไปเหมือนเดิม
 */
const PYDANTIC_TH: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [/^String should have at most (\d+) characters?$/i, (m) => `ความยาวต้องไม่เกิน ${m[1]} ตัวอักษร`],
  [/^String should have at least (\d+) characters?$/i, (m) => `ต้องมีอย่างน้อย ${m[1]} ตัวอักษร`],
  [/^value is not a valid email address.*$/i, () => 'รูปแบบอีเมลไม่ถูกต้อง'],
  [/^Input should be greater than or equal to (.+)$/i, (m) => `ต้องมีค่าตั้งแต่ ${m[1]} ขึ้นไป`],
  [/^Input should be less than or equal to (.+)$/i, (m) => `ต้องมีค่าไม่เกิน ${m[1]}`],
  [/^Input should be greater than (.+)$/i, (m) => `ต้องมีค่ามากกว่า ${m[1]}`],
  [/^Input should be less than (.+)$/i, (m) => `ต้องมีค่าน้อยกว่า ${m[1]}`],
  [/^Field required$/i, () => 'จำเป็นต้องกรอกข้อมูลนี้'],
  [/^Input should be a valid (integer|number).*$/i, () => 'ต้องเป็นตัวเลข'],
  [/^Input should be a valid date.*$/i, () => 'รูปแบบวันที่ไม่ถูกต้อง'],
  [/^Input should be a valid datetime.*$/i, () => 'รูปแบบวันที่-เวลาไม่ถูกต้อง'],
  [/^Input should be a valid boolean.*$/i, () => 'ต้องเป็นค่าใช่/ไม่ใช่'],
  [/^List should have at least (\d+) items?.*$/i, (m) => `ต้องมีอย่างน้อย ${m[1]} รายการ`],
  [/^List should have at most (\d+) items?.*$/i, (m) => `ต้องมีไม่เกิน ${m[1]} รายการ`],
];

const localizePydantic = (msg: string): string => {
  for (const [re, th] of PYDANTIC_TH) {
    const m = msg.match(re);
    if (m) return th(m);
  }
  return msg;
};

/**
 * Strip "Value error, " Pydantic prefix that prepends every value_error message,
 * then localize the built-in Pydantic English messages (BUG-S132-PYDANTIC-EN).
 */
const cleanMessage = (msg: string): string =>
  localizePydantic((msg || '').replace(/^Value error,\s*/, '').trim());

export function mapFieldErrors(error: unknown): FieldErrorMap | null {
  // axios error shape: error.response.data.error.details.field_errors
  const err = error as {
    response?: {
      data?: {
        error?: {
          details?: {
            field_errors?: Array<{ field?: string; message?: string; error_type?: string }>;
          };
        };
      };
    };
  };

  const fieldErrors = err?.response?.data?.error?.details?.field_errors;
  if (!Array.isArray(fieldErrors) || fieldErrors.length === 0) return null;

  const out: FieldErrorMap = {};
  for (const fe of fieldErrors) {
    // field looks like "body -> tax_id" → strip "body -> " → "tax_id"
    // nested (list) paths มาเป็น "body -> contacts -> 0 -> phone" → "contacts.0.phone"
    const raw = (fe.field || '').replace(/^body\s*->\s*/, '').trim();
    if (!raw) continue;
    const path = raw.split(/\s*->\s*/).filter(Boolean).join('.');
    if (!path) continue;
    const msg = cleanMessage(fe.message || '');
    // feedback ทิวากร 3 ก.ย. 2026: เดิม map เป็น camelCase อย่างเดียว แต่ฟอร์มในระบบใช้ key
    // ทั้งสองแบบ (SitePage/UserPage/MaintenanceTypePage = snake_case `project_phone`,
    // `company_id`, `full_name`, `role_id` · ProductPage = camelCase `monthlyRentalRate`)
    // → ฟอร์ม snake_case ไม่เคยได้ error ใต้ช่องเลย ผู้ใช้เห็นแต่ toast รวมว่า "ตรวจสอบข้อมูล
    // ที่กรอก" โดยไม่รู้ว่าช่องไหนผิด · ใส่ทั้งสอง key ให้ฟอร์มหยิบไปใช้ได้ทั้งคู่
    out[path] = msg;
    const camel = snakeToCamel(path);
    if (camel !== path) out[camel] = msg;
  }

  return Object.keys(out).length > 0 ? out : null;
}

/**
 * summarizeFieldErrors — ข้อความสรุปจาก 422 field_errors สำหรับ toast
 * (คืน null ถ้าไม่ใช่ error รูปแบบนี้) — ผ่าน localizePydantic ชุดเดียวกับ inline error
 */
export function summarizeFieldErrors(error: unknown, max = 3): string | null {
  const fieldErrors = (error as {
    response?: { data?: { error?: { details?: { field_errors?: Array<{ field?: string; message?: string }> } } } };
  })?.response?.data?.error?.details?.field_errors;
  if (!Array.isArray(fieldErrors) || fieldErrors.length === 0) return null;
  const msgs: string[] = [];
  for (const fe of fieldErrors) {
    const m = cleanMessage(fe.message || '');
    // KD3 S2492: ใส่ชื่อช่องนำหน้า (หาจาก element ที่ name ตรงกับ field ในฟอร์มที่เปิดอยู่) — เดิม toast ขึ้นแค่
    // "ความยาวต้องไม่เกิน 50 ตัวอักษร" ผู้ใช้ไม่รู้ว่าช่องไหนจากสิบกว่าช่อง
    const path = (fe.field || '').replace(/^body\s*->\s*/, '').split(/\s*->\s*/).filter(Boolean).join('.');
    const label = path ? (fieldLabelFor(path) || fieldLabelFor(snakeToCamel(path))) : null;
    const line = m && label ? `${label}: ${m}` : m;
    if (line && !msgs.includes(line)) msgs.push(line);
  }
  if (msgs.length === 0) return null;
  const shown = msgs.slice(0, max).join(' · ');
  return msgs.length > max ? `${shown} (และอีก ${msgs.length - max} รายการ)` : shown;
}

/**
 * getApiErrorMessage — extract the most specific human-readable message from a
 * KRP API error for a toast, with graceful fallbacks.
 *
 * OBS-S457-001 fix: many catch blocks read `error.response.data.detail` (the
 * FastAPI default 422 shape) but KRP wraps errors in a custom envelope
 * (`error.message` / `error.details.field_errors[].message`), so `.detail` is
 * undefined and the user only ever sees a generic fallback toast. This reads the
 * KRP envelope first (specific field message → error.message → user_message),
 * then the legacy FastAPI shapes, then the caller's fallback.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    code?: string;
    message?: string;
    response?: {
      data?: {
        error?: {
          message?: string;
          user_message?: string;
          details?: { field_errors?: Array<{ message?: string }> };
        };
        detail?: string | Array<{ msg?: string }>;
        message?: string;
      };
    };
  };
  const data = err?.response?.data;
  // P-S043-1: ไม่มี response = ยิงไม่ถึง server (offline/timeout/DNS) — เดิมตกไปที่
  // fallback ของแต่ละหน้า ผู้ใช้เห็นข้อความ generic หรือ raw "Network Error" ทั้งระบบ
  if (!err?.response) {
    const raw = String(err?.message || '');
    if (err?.code === 'ECONNABORTED' || /timeout/i.test(raw)) {
      return 'การเชื่อมต่อใช้เวลานานเกินไป กรุณาลองใหม่อีกครั้ง';
    }
    if (/network error/i.test(raw) || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      return 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
    }
    return fallback;
  }
  if (!data) return fallback;

  // KRP standard envelope — prefer the specific field error, then envelope message(s)
  const fe = data.error?.details?.field_errors;
  if (Array.isArray(fe) && fe.length > 0 && fe[0]?.message) {
    return cleanMessage(fe[0].message);
  }
  // BUG-S2170-001: 5xx ของ backend (handle_unknown_error / database error) ส่ง message เป็นอังกฤษ
  // สำหรับ dev ("An internal server error occurred") คู่กับ user_message ภาษาไทยสำหรับผู้ใช้ — เดิมเลือก
  // message ก่อนทุกกรณี จอเลยขึ้นภาษาอังกฤษ · 4xx ยังใช้ message ก่อน (เหตุผลที่เจาะจงกว่า — BUG-S2158)
  const status = (err?.response as { status?: number } | undefined)?.status ?? 0;
  if (status >= 500 && data.error?.user_message) return data.error.user_message;
  if (data.error?.message) return data.error.message;
  if (data.error?.user_message) return data.error.user_message;

  // Legacy FastAPI shapes
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return cleanMessage(data.detail[0].msg);
  if (typeof data.message === 'string') return data.message;

  return fallback;
}

/**
 * BUG-S357-EXPORTERRMSG: เวอร์ชัน async สำหรับ request ที่ใช้ `responseType: 'blob'` (ปุ่มส่งออก
 * รายงานทุกหน้า). เมื่อ request แบบ blob ล้มเหลว axios จะคืน `error.response.data` เป็น **Blob**
 * ที่ข้างในเป็น JSON ของ error envelope — `getApiErrorMessage()` อ่านแบบ sync จึงมองไม่เห็น แล้ว
 * ตกไป fallback เสมอ ⇒ ผู้ใช้ที่ไม่มีสิทธิ์ส่งออกเห็น "กรุณาลองใหม่อีกครั้ง" ทั้งที่กดกี่ครั้งก็ 403.
 * อ่าน blob เป็น text → parse แล้วส่งต่อให้ getApiErrorMessage ตัวเดิม (กติกาการเลือกข้อความเดียวกัน).
 */
export async function getApiErrorMessageAsync(error: unknown, fallback: string): Promise<string> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return getApiErrorMessage({ response: { data: parsed } }, fallback);
    } catch {
      return fallback;
    }
  }
  return getApiErrorMessage(error, fallback);
}

/**
 * E2E UI-A A-04 (30 ก.ย. 2026): `name=` ของ SearchableSelect / MultiSearchableSelect อยู่บน
 * `<input type="hidden">` ซึ่งโฟกัสและเลื่อนหาไม่ได้ — ช่องบังคับที่เป็น dropdown (เช่น บริษัท) กดบันทึกแล้ว
 * โฟกัสค้างที่ปุ่ม ข้อความผิดพลาดอยู่นอกจอ · ปุ่มจริงคือ `[role=combobox]` ที่เป็นพี่น้องใน wrapper เดียวกัน
 * (มี `data-name` ตรงกับ name) → คืนปุ่มนั้นแทน
 *
 * ดูเฉพาะ "ลูกตรงของ wrapper เดียวกัน" — ไม่ไล่ขึ้นไปไกล เพราะ hidden input ทั่วไป (เช่น device_id ของหน้าเคสซ่อม)
 * จะไปคว้า dropdown ของช่องอื่นมาโฟกัสผิดช่อง · คืน null = ช่องนี้โฟกัสไม่ได้
 */
export function resolveFocusTarget(el: HTMLElement, field: string): HTMLElement | null {
  const isHiddenInput = el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'hidden';
  if (!isHiddenInput) return el;
  const siblings = Array.from(el.parentElement?.children ?? []) as HTMLElement[];
  const trigger =
    siblings.find((s) => s !== el && s.getAttribute('data-name') === field) ||
    siblings.find((s) => s !== el && s.getAttribute('role') === 'combobox');
  return trigger ?? null;
}

/**
 * Focus the first invalid field (WCAG 2.4.3 Focus Order) after rendering errors.
 * Uses getElementsByName which matches the `name` attribute used by form inputs.
 */
export function focusFirstInvalidField(fieldErrors: FieldErrorMap): void {
  // mapFieldErrors คืนทั้ง key แบบ snake_case และ camelCase ของ field เดียวกัน — ไล่หา key แรก
  // ที่มี element จริงในหน้า (ฟอร์มบางหน้าใช้ name เป็น snake_case บางหน้าเป็น camelCase)
  for (const field of Object.keys(fieldErrors)) {
    const el = document.getElementsByName(field)[0] as HTMLElement | undefined;
    if (!el) continue;
    const target = resolveFocusTarget(el, field);
    // ช่องที่โฟกัสไม่ได้เลย (hidden ที่ไม่มีปุ่มคู่) → ข้ามไปช่องถัดไปที่ผิด ดีกว่าหยุดแล้วเงียบ
    if (!target) continue;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.focus();
    break;
  }
  // KD3 S2111 (BUG-S2111-002): ผู้เรียกทุกหน้า set formErrors แล้ว return ก่อน toast — แต่หลายช่อง
  // ไม่มีที่แสดง formErrors.<ช่อง> (สินค้า: หน่วย/รายละเอียด · อุปกรณ์ 13 ช่อง · โครงการ 7 ช่อง ฯลฯ)
  // → กดบันทึกแล้วเงียบ ไม่รู้ว่าผิดตรงไหน · หลัง render ถ้าข้อความใดไม่ปรากฏบนจอ ให้ขึ้น toast แทน
  setTimeout(() => reportUnrenderedFieldErrors(fieldErrors), UNRENDERED_CHECK_DELAY_MS);
}

export const UNRENDERED_CHECK_DELAY_MS = 150;

/** ข้อความ field error ที่ไม่ปรากฏที่ไหนบนหน้าเลย (dedupe — snake/camel key ซ้ำข้อความเดียวกัน) */
export function unrenderedFieldErrorMessages(fieldErrors: FieldErrorMap, pageText: string): string[] {
  const unique = Array.from(new Set(Object.values(fieldErrors).filter(Boolean)));
  return unique.filter(msg => !pageText.includes(msg));
}

/**
 * KD3 S2492 (NEG-MD-034): toast สำรองเดิมขึ้นแค่ "ความยาวต้องไม่เกิน 50 ตัวอักษร" — ฟอร์มมีหลายช่อง
 * ผู้ใช้ไม่รู้ว่าช่องไหน · หาชื่อช่องจาก <label>/aria-label ของ element ที่ name ตรงกับ field แล้วใส่นำหน้า
 */
export function fieldLabelFor(field: string): string | null {
  if (typeof document === 'undefined') return null;
  const el = document.getElementsByName(field)[0] as HTMLInputElement | undefined;
  if (!el) return null;
  const text = el.labels?.[0]?.textContent || el.getAttribute('aria-label') || '';
  const clean = text.replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  return clean || null;
}

export function reportUnrenderedFieldErrors(fieldErrors: FieldErrorMap): void {
  if (typeof document === 'undefined') return;
  const missing = unrenderedFieldErrorMessages(fieldErrors, document.body?.innerText || document.body?.textContent || '');
  if (!missing.length) return;
  const lines = missing.map((msg) => {
    const field = Object.keys(fieldErrors).find((k) => fieldErrors[k] === msg && fieldLabelFor(k));
    const label = field ? fieldLabelFor(field) : null;
    return label ? `${label}: ${msg}` : msg;
  });
  showAlert('ข้อมูลไม่ถูกต้อง', lines.join('\n'), 'error');
}
